import { colord } from 'colord';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { generateGradientString } from '../../../../functions/utils/generateGradientString';
import { parseGradientString } from '../../../../functions/utils/parseGradientString';
import { useUIStore } from '../../../../hooks/stores/uiStore';
import { ColorPicker } from '../ColorPicker';
import { IconSelectInput } from '../IconSelectInput';
import { NumberInput } from '../NumberInput';
import { ColorPickerModalColorInputs } from './ColorPickerModalColorInputs';
import { ColorPickerModalColorSwatches } from './ColorPickerModalColorSwatches';
import { GradientSlider } from './GradientSlider';

export const GradientPage = ({
  value,
  onChange,
}: {
  value?: string | null;
  onChange: (newValue: string | undefined) => void;
}) => {
  const [UIData] = useUIStore();
  const [gradientData, setGradientData] = useState(parseGradientString(value || ''));
  const [activeColorIndex, setActiveColorIndex] = useState(0);

  const color = colord(gradientData.colorList[activeColorIndex]?.color || 'transparent');
  const rgba = color.toRgb();
  const hex = color.toHex();

  const updateGradientColor = (newValue?: string) => {
    if (!newValue) return;
    const newColors = [...gradientData.colorList];
    if (newColors.length === 0) return;

    newColors[activeColorIndex].color = newValue;
    setGradientData((prev) => ({ ...prev, colorList: newColors }));
  };

  const distributeStopsEvenly = () => {
    const newColors = gradientData.colorList.map((color, index) => {
      color.stop = +((index / (gradientData.colorList.length - 1)) * 100).toFixed(0);
      return color;
    });
    setGradientData((prev) => ({ ...prev, colorList: newColors }));
  };

  useEffect(() => {
    const gradientString = generateGradientString(gradientData);
    if (gradientString === value) return;

    onChange(gradientString);
  }, [gradientData]);

  // Set stops if all are null (case from the v1.0.0 config)
  useEffect(() => {
    const areAllStopsNull = gradientData.colorList.every((color) => color.stop === null);
    if (areAllStopsNull) {
      distributeStopsEvenly();
    }
  }, [gradientData]);

  useEffect(() => {
    setGradientData(parseGradientString(value || ''));
  }, [UIData.selectedElementIDs]);
  return (
    <>
      <ButtonsContainer>
        <div style={{ width: '70px' }}>
          <IconSelectInput
            value={gradientData.gradientType || undefined}
            data={[
              {
                value: 'linear-gradient',
                iconClassName: 'icon-gradient-linear',
              },
              {
                value: 'radial-gradient',
                iconClassName: 'icon-gradient-radial',
              },
            ]}
            onChange={(newValue) =>
              setGradientData((prev) => ({
                ...prev,
                gradientType: String(newValue),
              }))
            }
          />
        </div>
        {gradientData.gradientType === 'linear-gradient' && (
          <NumberInput
            iconClassName="icon-angle"
            value={gradientData.gradientDirection || undefined}
            onChange={(newValue) =>
              setGradientData((prev) => ({
                ...prev,
                gradientDirection: String(newValue),
              }))
            }
            defaultUnit="deg"
            minValue={0}
            maxValue={360}
            precision={0}
            inputStyle={{ textAlign: 'center' }}
            style={{ width: '85px' }}
          />
        )}
      </ButtonsContainer>
      <GradientSlider
        colorList={gradientData.colorList}
        setColors={(newColors) => {
          setGradientData((prev) => ({ ...prev, colorList: newColors }));
        }}
        activeColorIndex={activeColorIndex}
        setActiveColorIndex={setActiveColorIndex}
      />
      <ColorPicker rgba={rgba} onChange={updateGradientColor} />
      <ColorPickerModalColorInputs value={hex} onChange={updateGradientColor} />
      <ColorPickerModalColorSwatches onChange={updateGradientColor} />
    </>
  );
};

const ButtonsContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`;
